import { type TopicMessage, useWebSocketTopic } from '@src/shared/websocket';
import { useEffect, useRef } from 'react';

const REFRESH_DEBOUNCE_MS = 100;

/**
 * Subscribe to an invalidation topic and refresh a query on every message.
 *
 * The counterpart to a *snapshot* topic like `users.presence`, whose messages
 * carry the new state and are applied in place. An invalidation topic only
 * says "something changed", so the client has to go and ask -- which means
 * one event fans out into a refetch per connected browser. That cost is
 * accepted here because task and alert events are comparatively rare; do not
 * reach for this shape on a topic whose event rate scales with the audience.
 *
 * `onRefresh` fires on connect, after every message, and when the tab becomes
 * visible again -- the last of these even if the socket never dropped, since
 * a backgrounded tab can miss things the socket alone cannot detect.
 *
 * `onResync` fires on the same occasions *except* after a message: it is for
 * work that only makes sense when messages may have been missed. A subscriber
 * that already reacts to every message would otherwise do that work twice for
 * one change -- once on the message, once on the refresh that follows it.
 * Coalesced with `onRefresh`, so a reconnect and a message landing together
 * still produce one round of each.
 */
export default function useTopicRefresh(
  topic: string,
  onMessage: (message: TopicMessage) => void,
  onRefresh: () => void,
  onResync?: () => void,
) {
  const callbacks = useRef({ onMessage, onRefresh, onResync });
  callbacks.current = { onMessage, onRefresh, onResync };

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resyncPending = useRef(false);
  const scheduleRefresh = useRef((resync: boolean) => {
    if (resync) resyncPending.current = true;
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      const shouldResync = resyncPending.current;
      resyncPending.current = false;
      callbacks.current.onRefresh();
      if (shouldResync) callbacks.current.onResync?.();
    }, REFRESH_DEBOUNCE_MS);
  });

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') scheduleRefresh.current(true);
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  useWebSocketTopic(
    topic,
    (message) => {
      callbacks.current.onMessage(message);
      scheduleRefresh.current(false);
    },
    { onReconnect: () => scheduleRefresh.current(true) },
  );
}
